import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, UserProfile } from '../lib/supabase';

interface AuthContextType {
    user: User | null;
    session: Session | null;
    userProfile: UserProfile | null;
    signInWithMagicLink: (email: string) => Promise<{ error: any }>;
    signOut: () => Promise<void>;
    loading: boolean;
    /** True while the user's profile is being fetched. Distinct from `loading`
     * (auth) so consumers don't flash a paywall in the gap after auth resolves
     * but before `userProfile` arrives. */
    profileLoading: boolean;
    refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [profileLoading, setProfileLoading] = useState(true);

    const fetchUserProfile = async (userId: string) => {
        const startTime = Date.now();
        console.log('👤 [START] Fetching user profile for:', userId);
        setProfileLoading(true);

        try {
            // Add 10s timeout
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Profile fetch timeout after 10s')), 10000);
            });
            
            const queryPromise = supabase
                .from('user_profiles')
                .select('*')
                .eq('id', userId)
                .single();
            
            const result: any = await Promise.race([queryPromise, timeoutPromise]);
            const { data, error } = result;
            
            const elapsed = Date.now() - startTime;
            console.log(`👤 [COMPLETE] Query finished in ${elapsed}ms`);

            if (error) {
                console.error('❌ Error fetching user profile:', error);
                console.error('❌ Error code:', error.code);
                console.error('❌ Error message:', error.message);
                setUserProfile(null);
                return;
            }
            
            console.log('✅ User profile loaded successfully!');
            console.log('✅ Has paid:', data?.has_paid);
            console.log('✅ Assessment completed:', data?.assessment_completed);
            console.log('✅ Email:', data?.email);
            setUserProfile(data);
        } catch (error: any) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ [FAILED] Profile fetch failed after ${elapsed}ms:`, error.message);
            setUserProfile(null);
        } finally {
            setProfileLoading(false);
        }
    };

    const refreshProfile = async () => {
        if (user) {
            await fetchUserProfile(user.id);
        }
    };

    useEffect(() => {
        let mounted = true;
        
        console.log('🔐 AuthContext initializing...');

        // Check for PKCE token_hash in URL params
        const params = new URLSearchParams(window.location.search);
        const tokenHash = params.get('token_hash');
        const type = params.get('type');

        // Listen for auth state changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            if (!mounted) return;
            
            console.log('🔔 Auth event:', event);
            
            if (session?.user) {
                console.log('✅ User authenticated:', session.user.email);
                setSession(session);
                setUser(session.user);
                setLoading(false);
                fetchUserProfile(session.user.id);
            } else {
                console.log('ℹ️ No session');
                setSession(null);
                setUser(null);
                setUserProfile(null);
                setLoading(false);
                setProfileLoading(false);
            }
        });

        // Handle PKCE magic link (token_hash in URL)
        if (tokenHash && type === 'email') {
            console.log('🔗 PKCE Magic link detected, verifying...');
            supabase.auth.verifyOtp({
                token_hash: tokenHash,
                type: 'email',
            }).then(({ data, error }) => {
                if (!mounted) return;
                
                if (error) {
                    console.error('❌ Error verifying OTP:', error);
                    setLoading(false);
                    return;
                }
                
                console.log('✅ OTP verified, session created');
                console.log('✅ User:', data.user?.email);
                
                // onAuthStateChange will handle setting state and fetching profile
                
                // Clean URL params
                window.history.replaceState(null, '', window.location.pathname);
            });
        } else {
            // Check for existing session
            console.log('🔐 Checking for existing session...');
            console.log('🔐 LocalStorage supabase keys:', Object.keys(localStorage).filter(k => k.includes('supabase')));
            
            supabase.auth.getSession().then(({ data: { session }, error }) => {
                if (!mounted) return;
                
                if (error) {
                    console.error('❌ Error getting session:', error);
                    setLoading(false);
                    setProfileLoading(false);
                    return;
                }
                
                if (session?.user) {
                    console.log('✅ Existing session found!');
                    console.log('✅ User email:', session.user.email);
                    console.log('✅ User ID:', session.user.id);
                    console.log('✅ Session expires:', new Date(session.expires_at! * 1000));
                    setSession(session);
                    setUser(session.user);
                    setLoading(false); // Stop loading immediately
                    
                    // Session already in localStorage, fetch profile immediately
                    console.log('👤 Calling fetchUserProfile (existing session)...');
                    fetchUserProfile(session.user.id);
                } else {
                    console.log('ℹ️ No existing session found');
                    console.log('ℹ️ User will need to log in');
                    setLoading(false);
                    setProfileLoading(false);
                }
            }).catch((err) => {
                console.error('❌ Exception in getSession:', err);
                setLoading(false);
                setProfileLoading(false);
            });
        }

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const signInWithMagicLink = async (email: string) => {
        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                emailRedirectTo: `${window.location.origin}/login`,
            },
        });
        return { error };
    };

    const signOut = async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    };

    const value = useMemo(
        () => ({
            user,
            session,
            userProfile,
            signInWithMagicLink,
            signOut,
            loading,
            profileLoading,
            refreshProfile,
        }),
        [user, session, userProfile, loading, profileLoading]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

